import React, { useMemo } from 'react';

const VeggieProgressImageComponent = ({
                                          plantImageUrl,
                                          plantName,
                                          plantedDate,
                                          daysToMaturity,
                                          plantStatus = 'growing',
                                          size = 185 // Default size, can be customized
                                      }) => {
    // Calculate progress percentage
    const progress = useMemo(() => {
        if (!plantedDate || !daysToMaturity) return 0;

        const planted = new Date(plantedDate);
        const today = new Date();
        const daysPassed = Math.floor((today - planted) / (1000 * 60 * 60 * 24));

        return Math.min(100, Math.max(0, (daysPassed / daysToMaturity) * 100));
    }, [plantedDate, daysToMaturity]);

    // Status color mapping
    const statusColors = useMemo(() => ({
        'planned': '#6b7280',
        'planted': '#f59e0b',
        'Germination': '#10b981',
        'Sprouted': '#22c55e',
        'Seedling': '#16a34a',
        'Established': '#3b82f6',
        'Vegetative': '#047857',
        'Growing': '#047857',
        'Budding': '#ec4899',
        'Flowering': '#d946ef',
        'Fruiting': '#ea580c',
        'Podding': '#ea580c',
        'Ripening': '#ef4444',
        'Maturing': '#ef4444',
        'Harvest': '#f59e0b',
        'Post-Harvest': '#9ca3af',
        'Dormant': '#9ca3af',
        'Regrowing': '#9ca3af',
        'Dead': '#dc2626',
        'Dying': '#dc2626',
        'Removed': '#dc2626',
        'Early Vegetative': '#166534',
        'Late Vegetative': '#065f46',
        'Root Maturation': '#92400e',
        'Dormancy': '#4b5563',
        'Reproductive Growth': '#7c3aed',
        'Tuber Initiation': '#9a3412',
        'Tuber Bulking': '#ca8a04',
        'Seed Formation': '#a16207',
        'Root Development': '#7c2d12',
        'Cold Stratification': '#1e40af',
        'Shoot Emergence': '#065f46',
        'Bulb Regeneration': '#6b21a8'
    }), []);

    // const statusColor = statusColors[plantStatus?.toLowerCase()] || '#28a644';
    const statusColor = statusColors[plantStatus] || '#28a644';

    // Create conic gradient background for progress
    const progressBackground = progress > 0
        ? `conic-gradient(
        from -90deg,
        ${statusColor} 0deg ${progress * 3.6}deg,
        #e5e7eb ${progress * 3.6}deg 360deg
      )`
        : '#e5e7eb';

    const containerStyle = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: '50%',
        position: 'relative',
        margin: '0 auto',
        background: progressBackground,
        transition: 'all 0.3s ease',
        animation: plantStatus === 'Harvest' ? 'gentleRotate 4s ease-in-out infinite' : 'none'
    };

    const innerStyle = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: `${size - 30}px`,
        height: `${size - 30}px`,
        backgroundColor: '#f8f9fa',
        borderRadius: '50%',
        position: 'relative'
    };

    const imageStyle = {
        width: '125px',
        height: '125px',
        objectFit: 'cover',
        filter: 'drop-shadow(0px 0px 5px rgba(0,0,0,0.3))',
        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
    };

    const progressTextStyle = {
        position: 'absolute',
        top: '8px',
        right: '8px',
        background: 'rgba(255, 255, 255, 0.9)',
        borderRadius: '12px',
        padding: '2px 6px',
        fontSize: '14px',
        fontWeight: 'bold',
        color: statusColor,
        backdropFilter: 'blur(4px)',
        zIndex: 10,
        transition: 'all 0.3s ease',
        animation: 'popIn 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards',
        transformOrigin: 'center'
    };

    return (
        <div className="veggie-progress-container">
            <style>{`
        @keyframes popIn {
          from { transform: scale(0); }
          to { transform: scale(1); }
        }
        
        @keyframes gentleRotate {
          0%, 100% { transform: rotate(0deg); }
          50% { transform: rotate(5deg); }
        }
        
        .veggie-progress-container {
            transition: 0.3s ease;
            will-change: transform;
            transform: translateZ(0); /* Force hardware acceleration */
        }
        
        .veggie-progress-container:hover .veggie-image {
          transform: scale(1.05);
        }
        
        .veggie-progress-container:hover .progress-text {
          transform: scale(1.1);
          background: rgba(255, 255, 255, 1);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }
        
        .veggie-image {
            transform-origin: center center;
            will-change: transform;
            transform: translateZ(0); /* Force hardware acceleration */
            backface-visibility: hidden; /* Prevent flicker */
        }
        
        .progress-text {
            will-change: transform;
            transform: translateZ(0); /* Force hardware acceleration */
        }
        
        .veggie-progress-container:hover {
          transform: scale(1.02);
        }
      `}</style>

            <div style={containerStyle}>
                <div style={innerStyle}>
                    <img
                        src={plantImageUrl}
                        alt={plantName}
                        className="veggie-image"
                        style={imageStyle}
                        onError={(e) => {
                            e.target.src = 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=100&h=100&fit=crop';
                        }}
                    />
                </div>

                {progress > 0 && (
                    <div className="progress-text" style={progressTextStyle}>
                        {Math.round(progress)}%
                    </div>
                )}
            </div>
        </div>
    );
};

export default VeggieProgressImageComponent;